import axios from "axios";
import { API_BASE } from "../config.js";
import { getAccessToken } from "../auth/apiAuth.jsx";

const api = axios.create({
  baseURL: `${API_BASE}/api/data`,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const tk = getAccessToken && getAccessToken();
  if (tk) config.headers.Authorization = `Bearer ${tk}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      let msg =
        data?.error?.message ||
        (typeof data?.error === "string" ? data.error : null) ||
        data?.detail ||
        data?.message ||
        "Unknown error";
      const e = new Error(msg);
      e.status = status;
      e.details = data;
      if (status === 401) e.message = "Your session has expired. Please sign in again.";
      return Promise.reject(e);
    }
    return Promise.reject(error);
  }
);

/**
 * Get list of items/products with optional filtering
 */
export async function getItems({ search, category, criticality, company, page, pageSize } = {}) {
  const params = {};
  if (search) params.search = search;
  if (category) params.category = category;
  if (criticality) params.criticality = criticality;
  if (company) params.company = company;
  if (page) params.page = page;
  if (pageSize) params.pageSize = pageSize;

  const response = await api.get("/products", { params });
  return response.data;
}

/**
 * Get item by item number
 */
export async function getItemById(itemNo) {
  const response = await api.get(`/products/${encodeURIComponent(itemNo)}`);
  return response.data;
}

/**
 * Create a new item
 */
export async function createItem(itemData) {
  const response = await api.post("/products", itemData);
  return response.data;
}

/**
 * Update item fields
 */
export async function updateItem(itemNo, updateData) {
  const response = await api.patch(`/products/${encodeURIComponent(itemNo)}`, updateData);
  return response.data;
}

/**
 * Get item ledger entries
 */
export async function getItemLedgerEntries(itemNo, { filter, top } = {}) {
  const params = {};
  if (top) params.top = top;
  const response = await api.get(`/products/${encodeURIComponent(itemNo)}/ledger`, { params });
  return response.data;
}

/**
 * Get items by category
 */
export async function getItemsByCategory(categoryCode, options = {}) {
  return getItems({ ...options, category: categoryCode });
}

/**
 * Get items with low inventory
 */
export async function getLowInventoryItems(threshold = 10, options = {}) {
  return getItems({ ...options });
}
